import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { timeout } from "rxjs/operators";
import * as moment from "moment";
import { AppService } from "src/_services/app.service";
import {
  mtsDarkMode,
  setMtsDarkMode,
  clampMtsRange,
  MTS_MAX_RANGE_DAYS,
} from "../mts-reporting-summary/mts-theme";

// The shared HTTP interceptor returns the raw error from its catchError rather
// than an observable, so on a failed request neither subscribe callback is ever
// called. Without this the page sits on "Loading…" for ever.
const REQUEST_TIMEOUT_MS = 30000;

// Matches the API's own default - see displayCountFor. A cap that cannot be
// cleared needs a value to fall back to when a select hands back a blank.
const defaultRowCap = 20;

interface Row {
  key: string;
  label: string;

  acceptedBets: number;
  acceptedTurnover: number;
  stake: number;
  averageStake: number;
  unsettledBets: number;
  unsettledTurnover: number;

  rejectedBets: number;
  rejectedTurnover: number;
  rejectedPct: number;
  cancelledBets: number;
  cancelledTurnover: number;

  settledBets: number;
  settledTurnover: number;
  won: number;
  lost: number;
  winRatePct: number;
  cashOuts: number;
  cashOutAmount: number;

  pnl: number;
  marginPct: number;
  marginOnSettled: number;

  voided: number;
  voidedTurnover: number;
}

interface Column {
  key: keyof Row;
  label: string;
  kind: "money" | "int" | "pct";
  signed?: boolean;
  hint?: string;
}

// THE ACCEPTANCE SIDE, plus what was refused or pulled back. One list, and a
// short one on purpose.
//
// The settlement measures - settled bets and turnover, won, lost, win rate,
// cash-outs and the margin over settled turnover - are all SERVED, and the
// backend goes on storing them. They are simply not shown here. This page
// answers what the book TOOK in a period and what it has not yet resolved;
// adding the outcome columns beside that doubles the width to answer a
// different question nobody opened this page for.
//
// There used to be an Accepted/Settled toggle with a column list each. When
// the settled slot was removed the two lists were merged into one, which was
// wrong in the other direction - it put every settlement column on a page that
// had never shown them. This is the accepted list it always had, with the
// rejection and cancellation columns added.
const COLUMNS: Column[] = [
  { key: "acceptedBets", label: "Accepted Bets", kind: "int" },
  {
    key: "acceptedTurnover",
    label: "Accepted Turnover",
    kind: "money",
    hint: "Money at risk: the stake for a BACK, the liability for a LAY.",
  },
  { key: "stake", label: "Stake", kind: "money" },
  { key: "averageStake", label: "Avg Stake", kind: "money" },
  {
    key: "rejectedBets",
    label: "Rejected",
    kind: "int",
    hint: "Bets the book refused. They never existed, so they are counted apart from the rest.",
  },
  {
    key: "rejectedPct",
    label: "Rejected %",
    kind: "pct",
    hint: "Rejections over everything attempted - accepted plus rejected.",
  },
  { key: "rejectedTurnover", label: "Rejected Turnover", kind: "money" },
  {
    key: "cancelledBets",
    label: "Cancelled",
    kind: "int",
    hint: "Placed and then pulled back. Not the same as rejected, and not the same as voided.",
  },
  {
    key: "cancelledTurnover",
    label: "Cancelled Turnover",
    kind: "money",
    hint: "What those bets had put at risk before they were pulled back.",
  },
  { key: "unsettledBets", label: "Unsettled Bets", kind: "int" },
  { key: "unsettledTurnover", label: "Unsettled Turnover", kind: "money" },
  {
    key: "pnl",
    label: "P&L",
    kind: "money",
    signed: true,
    hint: "The BOOK's, not the player's: positive means the house is up.",
  },
  { key: "marginPct", label: "Margin %", kind: "pct", signed: true },
];

// NOT built, and each for a reason rather than for now:
//
//   Expected P&L / Efficiency %  need the price or overround each bet was
//                                taken at. Nothing in this path stores it, so
//                                there is no figure to derive them from.
//   Rejected / Junk bets         a rejected bet never becomes a bet, so it
//                                never reaches the reporting path at all.
//   Category                     available on the event document, but it is
//                                not stored on a row today.
//   Completion date              there is no date on which a period is known
//                                to be finished.
//
// Showing any of them as a blank column would suggest the number exists and
// happens to be missing, which is a worse answer than not offering it.

// The drill-down. The level name is the groupBy the API takes, so there is no
// mapping between what the page is showing and what it asked for.
type LevelName =
  | "sport"
  | "competition"
  | "event"
  | "market"
  | "livepre"
  | "bets"
  | "rejectedbets";

interface Level {
  level: LevelName;
  title: string;
  heading: string;
}

// The columns that are RATIOS of other columns, not quantities. They cannot be
// added when rows are merged - the sum of two averages is not an average - so
// they are recomputed from the merged figures instead. See rederive.
const DERIVED_COLUMNS: { [key: string]: boolean } = {
  averageStake: true,
  rejectedPct: true,
  marginPct: true,
  // Additive in principle - accepted minus settled on each side sums the same
  // as on the whole - but recomputed with the rest so there is one definition
  // of it on this page rather than two that could drift.
  unsettledBets: true,
  winRatePct: true,
};

const LEVELS: Level[] = [
  { level: "sport", title: "Sports", heading: "Sport" },
  { level: "competition", title: "Tournaments", heading: "Tournament" },
  { level: "event", title: "Events", heading: "Event" },
  // The level below an event. The API groups it by market id, as it does
  // every other level; the rows are merged by NAME here - see mergeByName.
  { level: "market", title: "Markets", heading: "Market" },
  // OUTSIDE THE CASCADE. The other three are a hierarchy - a competition sits
  // under a sport, an event under a competition - and the level below is
  // disabled until its parent is a single choice. Live/pre-match is not in
  // that tree: it is a property of the BET, so it is available at any time and
  // drills into nothing.
  { level: "livepre", title: "Pre-Match / Live", heading: "Pre-Match / Live" },
  // TICKET LEVEL, not a breakdown at all: one row per bet rather than per
  // group, so it has its own table and its own request. It is here because to
  // a reader it is the same report seen at its finest grain - the same filters
  // over the same period - and splitting it onto another page would mean
  // setting those filters twice.
  { level: "bets", title: "Accepted Bets", heading: "Date / Time" },
  // The other half of the ticket level. A refusal never became a bet, so it
  // lives in its own collection and carries a reason the accepted list has no
  // column for - but it is the same table otherwise, and the same request.
  { level: "rejectedbets", title: "Rejected Bets", heading: "Date / Time" },
];

interface BetRow {
  betId: string;
  placedAt: number;
  sport: string;
  event: string;
  market: string;
  selection: string;
  betType: string;
  livePre: string;
  odds: number;
  stake: number;
  status: string;
  counted: string;
  pnl: number;
  reasonCode: number;
  reason: string;
}

// What a live/pre-match row is called. The API returns the stored dimension,
// which is the bet's own value and not a label.
//
// The empty one is NOT "none" or a blank: it is a bet whose event start could
// not be resolved, so it is in the sport's total and in neither sub-table. A
// blank cell would read as a missing number rather than a real group.
const LIVE_PRE_LABELS: { [k: string]: string } = {
  LIVE: "Live",
  PREMATCH: "Pre-Match",
  MIXED: "Mixed",
  "": "Unclassified",
};

@Component({
  selector: "app-mts-report-generator",
  templateUrl: "./mts-report-generator.component.html",
  styleUrls: ["./mts-report-generator.component.scss"],
})
export class MtsReportGeneratorComponent implements OnInit {
  // SAPAdmin and nothing else, matching the API - see the summary page. The
  // sidebar hides the link, but no route in this app carries a role, so a
  // typed URL reaches the component regardless.
  canView = (JSON.parse(sessionStorage.getItem("currentUser")) || {}).role === "SAPAdmin";

  dark = mtsDarkMode();

  toggleTheme() {
    this.dark = !this.dark;
    setMtsDarkMode(this.dark);
  }

  levels = LEVELS;

  // The level the user has drilled TO. Read it through `level`, never
  // directly: on its own it can disagree with the filters, and three separate
  // bugs came out of that.
  private requestedLevel: LevelName = "sport";

  selected: any = {
    startDate: moment().startOf("day"),
    endDate: moment().endOf("day"),
  };
  rangeNote = "";

  ranges: any = {
    Today: [moment().startOf("day"), moment().endOf("day")],
    Yesterday: [
      moment().subtract(1, "days").startOf("day"),
      moment().subtract(1, "days").endOf("day"),
    ],
    "Last 7 Days": [moment().subtract(6, "days").startOf("day"), moment().endOf("day")],
    // NO 30-day or This Month preset. The API refuses a range longer than 7
    // days, because the cost of a report is the number of rows in it and a
    // month of them is between 6 and 25 seconds against a 15-second read
    // timeout. Offering a preset the API will reject means handing someone an
    // error for picking what the page put in front of them.
  };

  // ---- filters ----
  //
  // The option lists are built from the REPORT ITSELF rather than from the
  // catalog endpoints. Those take a provider id, which a report spanning
  // providers does not have; and a catalog list would offer every sport that
  // exists rather than the ones with bets in the range, so most of it would
  // select nothing. Asking the same API for a breakdown at that level gives
  // exactly the values that have data, already named, with no new endpoint.
  operatorData: any[] = [];
  selectedOperators: any[] = [];
  operatorSettings = {
    singleSelection: false,
    idField: "operator_id",
    textField: "operator_id",
    selectAllText: "Select All",
    unSelectAllText: "UnSelect All",
    itemsShowLimit: 2,
    allowSearchFilter: true,
  };

  sportOptions: any[] = [];
  competitionOptions: any[] = [];
  eventOptions: any[] = [];
  selectedSports: any[] = [];
  selectedCompetitions: any[] = [];
  selectedEvents: any[] = [];

  // The option objects are {id, name}, so the control shows the name and the
  // payload carries the id.
  idNameSettings = {
    singleSelection: false,
    idField: "id",
    textField: "name",
    selectAllText: "Select All",
    unSelectAllText: "UnSelect All",
    itemsShowLimit: 2,
    allowSearchFilter: true,
  };

  // Set when the page was opened from a day drill-down. While it holds, the
  // request sends `day` and the server anchors the range in the reporting
  // timezone. Any change to the date picker clears it and the page goes back
  // to sending an explicit range.
  day = 0;

  livePre: "" | "PREMATCH" | "LIVE" = "";


  // Singles, multiples (combos), or both together - the same three columns the
  // provider's own report puts side by side.
  //
  // "S" and "M" are what the API takes. A single carries no marker at all in
  // the stored rows, which is what keeps its row identical to every one
  // written before combos were split across their legs, so "singles" has to be
  // asked for by name rather than by an empty value - empty here means BOTH.
  betKind: "" | "S" | "M" = "";

  // The SINGLES figures for the same rows, when both bands are shown. Keyed by
  // the row's own key so a sport that has only combos still appears, with an
  // empty Singles side rather than a shifted one.
  singlesByKey: { [key: string]: any } = {};
  singlesTotals: any = null;

  // The column BANDS the table draws, left to right.
  //
  // With the filter on All - its default, nothing selected - there are two:
  // "Multiples & Singles" beside "Singles", which is how the provider's own
  // report reads. Narrowed to one kind there is one, because a second would
  // repeat the first. So the way to see both is to clear the filter, which is
  // what All already means.
  get bands(): { title: string; singles: boolean }[] {
    // shownBetKind, not betKind. The dropdown is what the NEXT report will
    // ask for; the bands label the one on screen. Reading the live value
    // relabelled the column "Multiples" over figures that were still
    // Multiples & Singles, until Generate was pressed.
    if (this.shownBetKind === "S") {
      return [{ title: "Singles", singles: false }];
    }
    if (this.shownBetKind === "M") {
      return [{ title: "Multiples", singles: false }];
    }
    return [
      { title: "Multiples & Singles", singles: false },
      { title: "Singles", singles: true },
    ];
  }

  // Same reason as the account cards: the getter above is a fresh array of
  // fresh objects every pass, and without this every cell in the table is
  // destroyed and rebuilt on each change-detection tick.
  trackBand(_: number, band: { title: string }): string {
    return band.title;
  }

  // The figures a band shows for a row. The singles band reads its own set;
  // everything else reads the row the table is iterating.
  cellRow(row: any, band: { singles: boolean }): any {
    if (!band.singles) {
      return row;
    }
    return this.singlesByKey[this.keyOf(row)] || null;
  }

  bandTotals(band: { singles: boolean }): any {
    return band.singles ? this.singlesTotals : this.totals;
  }

  // A day report keys on the day; everything else on the grouped value.
  private keyOf(r: any): string {
    return r && r.key ? String(r.key) : String((r && r.day) || "");
  }
  // REQUIRED, and defaulted rather than blank. Exchange and SportsBook do not
  // share a sport id space - cricket is "4" on BetFair and "sr:sport:21" on
  // SportRadar - so an unscoped report shows Cricket as two rows and the sport
  // filter offers it twice. The API refuses a request without it.
  groupProvider: "Exchange" | "SportsBook" = "SportsBook";

  rows: Row[] = [];
  totals: Row = null;
  loading = false;
  errorMessage = "";

  // A response is only applied while it is still the LATEST request of its
  // kind. Nothing here cancels an in-flight call, so two quick changes leave
  // two requests racing - and if the first is the slower it lands last and
  // wins. On the table that means rows that do not match the filters above
  // them, with nothing on the page saying so; on a dropdown it means options
  // from a filter the user has already moved past.
  //
  // One counter per kind rather than one for the page: the report and the
  // three option lists are independent, and a dropdown that resolves late
  // must not be discarded because the table was reloaded after it.
  private reportSeq = 0;
  private optionSeq: { [k: string]: number } = { sport: 0, competition: 0, event: 0 };

  constructor(
    private appService: AppService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    if (!this.canView) {
      return;
    }
    // Arriving from a sport row on the day drill-down: the sport and the day
    // come in the URL so the page opens on the same book the click was made
    // in, and so the link survives a reload and can be shared.
    const qp = this.route.snapshot.queryParamMap;
    // The reporting DAY, passed through as a day - not converted to a range.
    //
    // A reporting day is anchored in the reporting timezone (IST) by
    // DaySlotRange server-side. Turning it into moment().startOf("day") here
    // anchors it in the BROWSER's timezone instead, so a browser outside IST
    // asked for a different span of hours than the day page it was opened
    // from: the two pages reported overlapping but different cohorts for what
    // the UI calls the same day.
    this.day = Number(qp.get("day")) || 0;
    if (this.day > 0) {
      const d = moment(String(this.day), "YYYYMMDD");
      if (d.isValid()) {
        // Shown in the picker so the range reads correctly; the request sends
        // `day`, not these.
        this.selected = { startDate: d.startOf("day"), endDate: d.clone().endOf("day") };
      }
    } else {
      // A RANGE, which the Profitability Report sends when it is showing one
      // rather than a single day. Without this the drill-through landed on
      // the generator's own default period, so clicking a sport silently
      // changed the dates underneath the figures that were just clicked.
      const from = Number(qp.get("startTime")) || 0;
      const to = Number(qp.get("endTime")) || 0;
      if (from > 0 && to >= from) {
        this.selected = clampMtsRange(moment(from * 1000), moment(to * 1000));
      }
    }
    const gp = qp.get("groupProvider");
    if (gp === "Exchange" || gp === "SportsBook") {
      this.groupProvider = gp;
    }
    const bk = (qp.get("betKind") || "").toUpperCase();
    if (bk === "S" || bk === "M") {
      this.betKind = bk;
    }
    const lp = (qp.get("livePre") || "").toUpperCase();
    if (lp === "LIVE" || lp === "PREMATCH") {
      this.livePre = lp;
    }
    const ops = (qp.get("operators") || "").split(",").filter((x) => !!x);
    this.selectedOperators = ops.map((id) => ({ operator_id: id }));

    const sport = qp.get("sportId") || "";
    const sportName = qp.get("sportName") || sport;
    if (sport) {
      // Seeded from the URL so the control shows it before the option list has
      // loaded; loadSportOptions replaces it with the resolved name.
      this.selectedSports = [{ id: sport, name: sportName }];
      // A sport was chosen, so the useful first view is its competitions -
      // which is what the click on the summary page meant.
      this.requestedLevel = "competition";
    }

    this.loadOperators();
    // ALWAYS. This used to be skipped when the report was grouped by sport, on
    // the grounds that the breakdown filled the dropdown for free - but the
    // sport list is the catalog now, which is a different and fuller list
    // (every sport in the book, not only the ones with bets in the range), and
    // the two were racing to fill the same control. The catalog wins, so it
    // has to be fetched. It is a few dozen cached rows, not an aggregation.
    this.loadSportOptions();
    if (sport) {
      this.loadCompetitionOptions();
    }
    this.load();
  }

  // ---- the cascade ----
  //
  // A breakdown below a level needs ONE parent. Picking two sports and then
  // filtering by competition asks for competitions across two sports with no
  // single parent to drill from, and a list that mixed them would read as one
  // tournament tree. So the level below is disabled while more than one is
  // selected here, and anything already chosen in it is cleared - leaving a
  // stale selection applied but invisible is worse than losing it.
  get competitionDisabled(): boolean {
    return this.selectedSports.length !== 1;
  }

  get eventDisabled(): boolean {
    return this.competitionDisabled || this.selectedCompetitions.length !== 1;
  }

  // The group decides which sport id space the report is in, so changing it
  // invalidates every selection below it - a SportRadar sport id means nothing
  // to an Exchange report.
  onGroupProviderChange() {
    this.selectedSports = [];
    this.selectedCompetitions = [];
    this.selectedEvents = [];
    this.competitionOptions = [];
    this.eventOptions = [];
    this.requestedLevel = "sport";
    // The sport LIST still reloads - it is a filter's options, not the report,
    // and an empty dropdown is not something to make anyone press Generate
    // for. The table waits, like every other filter.
    this.loadSportOptions();
    this.markDirty();
  }

  onSportChange() {
    this.selectedCompetitions = [];
    this.selectedEvents = [];
    this.competitionOptions = [];
    this.eventOptions = [];
    if (!this.competitionDisabled) {
      this.loadCompetitionOptions();
    }
    this.markDirty();
  }

  onCompetitionChange() {
    this.selectedEvents = [];
    this.eventOptions = [];
    if (!this.eventDisabled) {
      this.loadEventOptions();
    }
    this.markDirty();
  }

  // ---- drill-down ----

  // The level actually being shown: what was asked for, CLAMPED to what the
  // filters can support. A breakdown below a level needs exactly one parent,
  // so without the clamp the page could sit in states that cannot be
  // rendered honestly - and did:
  //
  //   - at event level, adding a second sport left level="event" with no
  //     competition filter, so the table was every event across two sports
  //     under an "Events" heading. The breadcrumb is built from the same
  //     clamp, so at that point it offered no clickable crumb either and the
  //     only way out was Reset.
  //   - clicking a row then drilled from a level the filters did not support,
  //     and basePayload dropped the id that had just been clicked.
  //
  // Deriving it removes all of that by construction rather than by remembering
  // to reset the level everywhere a filter changes.
  get level(): LevelName {
    // Not in the hierarchy, so nothing above has to be chosen first. Bets is
    // not even a grouping - it is every bet the filters match.
    if (
      this.requestedLevel === "livepre" ||
      this.requestedLevel === "bets" ||
      this.requestedLevel === "rejectedbets"
    ) {
      return this.requestedLevel;
    }
    // Tournaments no longer needs a sport picked first - the API stopped
    // requiring one, because the seven-day range cap already bounds the row
    // count and a competition is a fraction of the events beneath it.
    if (this.requestedLevel === "sport" || this.requestedLevel === "competition") {
      return this.requestedLevel;
    }
    // EVENTS IS A REPORT IN ITS OWN RIGHT, like Tournaments. It used to fall
    // back to Tournaments unless exactly one competition was chosen, because
    // the API refused an unbounded event breakdown - it does not any more, the
    // seven-day range cap having made that rule redundant.
    //
    // Leaving the fallback in place after the API changed was worse than
    // useless: the Report box said Events and the table said Tournaments.
    return this.requestedLevel;
  }

  setLevel(level: LevelName) {
    if (this.requestedLevel === level) {
      return;
    }
    this.requestedLevel = level;
    // A page number from the previous list means nothing to a new one.
    this.betsPage = 1;
    this.rowPage = 1;
    this.markDirty();
  }

  // The label a row carries, which for live/pre-match is the dimension itself.
  rowLabel(r: Row): string {
    if (this.shownLevel !== "livepre") {
      return r.label || r.key || "-";
    }
    const k = (r.key || "").toUpperCase();
    return LIVE_PRE_LABELS[k] !== undefined ? LIVE_PRE_LABELS[k] : k;
  }

  // WHAT THE TABLE IS SHOWING, which is not the same as what the controls say
  // now that a change waits for Generate. Set when a load starts, so the
  // heading and the columns describe the request in flight rather than one
  // nobody has asked for yet.
  private shownLevel: LevelName = "sport";
  private shownBetKind = "";

  private isListLevel(l: LevelName): boolean {
    return l === "bets" || l === "rejectedbets";
  }

  get levelTitle(): string {
    return (LEVELS.find((l) => l.level === this.shownLevel) || LEVELS[0]).title;
  }

  get levelHeading(): string {
    return (LEVELS.find((l) => l.level === this.shownLevel) || LEVELS[0]).heading;
  }

  // A row is clickable only while there is a level below it to open.
  get canDrill(): boolean {
    return (
      this.shownLevel !== "market" &&
      this.shownLevel !== "livepre" &&
      !this.isBetList
    );
  }

  // A bet list is a different request with a different shape, so the page asks
  // which one it is showing rather than rendering an empty breakdown.
  get isBetList(): boolean {
    return this.isListLevel(this.shownLevel);
  }

  // A refusal carries a reason and no outcome, so that list drops the status
  // and P&L columns and gains two of its own.
  get isRejectedList(): boolean {
    return this.shownLevel === "rejectedbets";
  }

  // Clicking a row drills in: it becomes the filter at its own level, and the
  // report moves to the level below. That is the same thing the filter
  // dropdowns do, so a drill and a hand-made filter land on the same report.
  drill(row: Row) {
    if (!this.canDrill || !row.key) {
      return;
    }
    const picked = { id: row.key, name: row.label };
    // shownLevel, not level. canDrill gates on what the TABLE is showing, so
    // branching on the dropdown made clicking a Sports row while the dropdown
    // already said "Markets" fall through to the event branch and file a
    // SPORT id as the event filter - invisibly, because the Events box is
    // hidden at market level.
    if (this.shownLevel === "sport") {
      this.selectedSports = [picked];
      this.selectedCompetitions = [];
      this.selectedEvents = [];
      this.competitionOptions = [];
      this.eventOptions = [];
      this.requestedLevel = "competition";
      this.loadCompetitionOptions();
    } else if (this.shownLevel === "competition") {
      this.selectedCompetitions = [picked];
      this.selectedEvents = [];
      this.eventOptions = [];
      this.requestedLevel = "event";
      this.loadEventOptions();
    } else if (this.shownLevel === "event") {
      // An event opens its MARKETS. There is no option list to load: markets
      // are not a filter on this page, they are the last breakdown.
      this.selectedEvents = [picked];
      this.requestedLevel = "market";
    } else {
      // Nothing below a market, and nothing below the levels outside the
      // cascade. canDrill should already have stopped this.
      return;
    }
    this.load();
  }

  // The breadcrumb, which is also how you go back up: each crumb drops the
  // filters below it.
  // Built from the SAME clamp the table uses, so the trail can never claim a
  // level the table is not showing. It was derived from the selections alone,
  // which made it say "Sports / Cricket" - implying the competition level -
  // while the table was still grouped by sport.
  get crumbs(): { label: string; level: LevelName }[] {
    const lvl = this.level;
    const out: { label: string; level: LevelName }[] = [{ label: "Sports", level: "sport" }];
    if (lvl !== "sport" && this.selectedSports.length === 1) {
      out.push({ label: this.selectedSports[0].name, level: "competition" });
    }
    if ((lvl === "event" || lvl === "market") && this.selectedCompetitions.length === 1) {
      out.push({ label: this.selectedCompetitions[0].name, level: "event" });
    }
    // The event's own crumb, so a market breakdown can be left the same way it
    // was entered rather than only by Reset.
    if (lvl === "market" && this.selectedEvents.length === 1) {
      out.push({ label: this.selectedEvents[0].name, level: "market" });
    }
    return out;
  }

  goToLevel(l: LevelName) {
    if (l === this.level) {
      return;
    }
    this.requestedLevel = l;
    if (l === "sport") {
      this.selectedSports = [];
      this.selectedCompetitions = [];
      this.selectedEvents = [];
      this.competitionOptions = [];
      this.eventOptions = [];
    } else if (l === "competition") {
      this.selectedCompetitions = [];
      this.selectedEvents = [];
      this.eventOptions = [];
    } else if (l === "event") {
      // Going back up to the events of a tournament drops the one event that
      // was opened; without this the Events table would show the single event
      // the market breakdown came from.
      this.selectedEvents = [];
    }
    this.load();
  }

  // mergeByName folds market rows that share a NAME into one.
  //
  // One market of one event is many ids: the line is re-created as the book
  // moves and the providers re-key it when its specifiers change, so a match
  // can hold dozens of rows all called "1st innings - India total". The server
  // groups by id like every other level; the names are what a reader came for.
  //
  // Only at the market level. Everywhere else a key IS the thing, and two rows
  // sharing a label are two different things.
  //
  // A row with no name keeps its own id as the key: empty means the bet did
  // not say, never "the same market".
  private mergeByName(rows: Row[]): Row[] {
    if (this.shownLevel !== "market") {
      return rows;
    }
    // Object.create(null), not {}: a market named "constructor" or "toString"
    // finds a truthy inherited member on a plain object literal and is merged
    // into something that is not a row - or dropped from the report entirely.
    const byName: { [name: string]: Row } = Object.create(null);
    const order: string[] = [];
    for (const r of rows) {
      const name = (r.label || "").trim().replace(/\s+/g, " ") || String(r.key || "");
      const into = byName[name];
      if (!into) {
        byName[name] = { ...r, key: name, label: name };
        order.push(name);
        continue;
      }
      // EVERY numeric field, not just the rendered columns. rederive reads
      // settledBets, won and lost, and none of the three is a column - driving
      // the sum from COLUMNS left them at the first source row's values, so a
      // merged market reported its Unsettled Bets as if only one of the ids
      // behind it had ever settled.
      for (const key of Object.keys(r)) {
        if (DERIVED_COLUMNS[key] || typeof (r as any)[key] !== "number") {
          continue;
        }
        (into as any)[key] = ((into as any)[key] || 0) + ((r as any)[key] || 0);
      }
    }
    return order.map((name) => this.rederive(byName[name]));
  }

  // rederive recomputes the figures that are ratios of others, after a merge.
  private rederive(r: Row): Row {
    const row: any = r;
    row.averageStake = row.acceptedBets > 0 ? row.stake / row.acceptedBets : 0;
    const decided = (row.won || 0) + (row.lost || 0);
    row.winRatePct = decided > 0 ? (100 * row.won) / decided : 0;
    row.unsettledBets = (row.acceptedBets || 0) - (row.settledBets || 0);
    const attempted = (row.acceptedBets || 0) + (row.rejectedBets || 0);
    row.rejectedPct = attempted > 0 ? (100 * row.rejectedBets) / attempted : 0;
    row.marginPct = row.acceptedTurnover > 0 ? (100 * row.pnl) / row.acceptedTurnover : 0;
    return row;
  }

  // loadSinglesBand fills the second band. It runs only when both kinds are
  // shown, because a report already narrowed to one kind IS its band.
  //
  // A failure leaves the band empty rather than taking the page down: the
  // first band is the report, the second is a comparison beside it.
  private loadSinglesBand(payload: any, seq: number): void {
    this.singlesByKey = {};
    this.singlesTotals = null;
    if (this.shownBetKind !== "") {
      return;
    }
    // WITHOUT the cut. The server sorts by turnover and then pages, so asking
    // for "top 20" again on the singles side returns a DIFFERENT twenty - a
    // tournament big overall but 21st by singles came back absent, and the
    // table showed it a dash, which reads as "no singles" rather than "not
    // fetched". The band is indexed by key, so the extra rows cost a lookup
    // that never matches and nothing else.
    const singlesPayload = { ...payload, betKind: "S" };
    delete singlesPayload.displayCount;
    delete singlesPayload.page;
    this.appService
      .getMtsReportingSummary(singlesPayload)
      .pipe(timeout(REQUEST_TIMEOUT_MS))
      .subscribe(
        (res) => {
          // Superseded, exactly as the main request guards itself: a newer
          // report is on screen and this band belongs to the previous one.
          if (seq !== this.reportSeq || !res || res.status !== "RS_OK") {
            return;
          }
          const byKey: { [key: string]: any } = Object.create(null);
          // Merged the same way as the first band, or a market would key on
          // its name on one side and its id on the other and never line up.
          for (const r of this.mergeByName(res.rows || [])) {
            byKey[r.key ? String(r.key) : String((r as any).day || "")] = r;
          }
          this.singlesByKey = byKey;
          this.singlesTotals = res.totals || null;
        },
        () => {
          /* the band stays empty; the report itself is already on screen */
        }
      );
  }

  // ---- columns ----

  get visibleColumns(): Column[] {
    return COLUMNS;
  }

  onDateChangeHandler(ev: any) {
    if (!ev || !ev.startDate || !ev.endDate) {
      return;
    }
    this.selected = clampMtsRange(ev.startDate, ev.endDate);
    this.rangeNote =
      this.selected.startDate.isSame(moment(ev.startDate), "day")
        ? ""
        : `Ranges are limited to ${MTS_MAX_RANGE_DAYS} days - showing the last ${MTS_MAX_RANGE_DAYS}.`;
    // Picking a range replaces the inherited reporting day.
    this.day = 0;
    // The competition and event lists are breakdowns over the range, so a new
    // range makes the ones on screen stale - they offer what had bets in the
    // period the user has just moved away from. Picking one is not wrong, it
    // simply reports nothing, which reads as a quiet day rather than as a
    // stale dropdown.
    //
    // The report itself is NOT reloaded: that is what Generate is for, and
    // running it on every date change is the cost this page avoids. The
    // sport list is the catalog and does not move with the range.
    if (!this.competitionDisabled) {
      this.loadCompetitionOptions();
    }
    if (!this.eventDisabled) {
      this.loadEventOptions();
    }
    this.markDirty();
  }

  // ---- loading ----

  // The filters as the API takes them. One place, so the report and the three
  // option lists cannot disagree about what is being filtered.
  // How many rows to show, matching the reference console's control. 0 is all
  // of them.
  //
  // The TOTALS still cover every row - the API computes them before the cut -
  // so a capped report agrees with an uncapped one over the same filters. The
  // visible rows therefore do not add up to the total, and the page says so
  // rather than letting it look like an error.
  // TWENTY, as the reference console defaults it.
  //
  // The levels it applies to are the ones whose row count is not bounded by
  // anything: every tournament or event with a bet in the range. Opening one
  // of those on "All" is asking for the whole list before anyone has said they
  // want it - and the first thing a reader does with a long table is look at
  // the top of it anyway.
  displayCount = 20;
  // NO "ALL". These two levels grow with the book - every tournament or event
  // with a bet in the range - so "all of them" is a different number every day
  // and an unbounded one on prod. The API refuses it too; this is the half of
  // the guard that stops anyone asking.
  displayCounts = [10, 20, 50, 100];
  totalRows = 0;

  // These three MOVE AROUND a report that has already been asked for, rather
  // than describing a different one, so they fetch straight away - waiting for
  // Generate to turn a page would be a strange thing to have to do.
  setDisplayCount(n: any) {
    this.displayCount = Number(n) || defaultRowCap;
    // A page number from a different page size means nothing.
    this.rowPage = 1;
    this.load();
  }

  // Which page of a capped breakdown is showing. 1 unless the reader has
  // walked forward, and reset by anything that changes what is being counted.
  rowPage = 1;

  get rowPages(): number {
    if (!this.totalRows) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.totalRows / this.displayCount));
  }

  // The page numbers to draw: the first, the last, and a window round the
  // current one, with -1 standing for a gap. Fixed width whatever the total,
  // so the strip does not grow a page at a time on a long report.
  get pageStrip(): number[] {
    const last = this.rowPages;
    const cur = this.rowPage;
    if (last <= 7) {
      return Array.from({ length: last }, (_, i) => i + 1);
    }
    const out: number[] = [1];
    const from = Math.max(2, cur - 1);
    const to = Math.min(last - 1, cur + 1);
    if (from > 2) {
      out.push(-1);
    }
    for (let i = from; i <= to; i++) {
      out.push(i);
    }
    if (to < last - 1) {
      out.push(-2);
    }
    out.push(last);
    return out;
  }

  goToRowPage(p: number) {
    if (p < 1 || p > this.rowPages || p === this.rowPage) {
      return;
    }
    this.rowPage = p;
    this.load();
  }

  // WHICH LEVELS A ROW CAP MEANS ANYTHING FOR.
  //
  // Sports is a dozen rows and Pre-Match / Live is three or four; capping
  // either answers a question nobody has, and the control just sits there
  // reading "All". Tournaments and Events are the two that grow with the book,
  // which is why the reference console offers it on exactly those.
  // A DRILL-DOWN FILTER BELOW THE REPORT'S OWN LEVEL FILTERS NOTHING, and the
  // reference console does not offer it: its Sports report has a Sports box
  // and no Tournaments or Events, its Tournaments report adds Tournaments, and
  // only its Events report shows all three.
  //
  // Hidden rather than disabled. A disabled control is a promise that
  // something will enable it; these only ever apply to a different report.
  get showCompetitionFilter(): boolean {
    const l = this.requestedLevel;
    return l === "competition" || l === "event" || this.isListLevel(l) || l === "livepre";
  }

  get showEventFilter(): boolean {
    const l = this.requestedLevel;
    return l === "event" || this.isListLevel(l) || l === "livepre";
  }

  get showRowCap(): boolean {
    return this.level === "competition" || this.level === "event";
  }

  get isCapped(): boolean {
    return this.totalRows > 0 && this.totalRows > this.rows.length;
  }

  // ---- the ticket-level list ----

  bets: BetRow[] = [];
  betsLoading = false;
  betsError = "";
  betsPage = 1;
  betsPageSize = 20;
  betsTotal = 0;
  betsCapped = false;

  get betsPages(): number {
    return Math.max(1, Math.ceil(this.betsTotal / this.betsPageSize));
  }

  goToBetsPage(p: number) {
    if (p < 1 || p > this.betsPages || p === this.betsPage) {
      return;
    }
    this.betsPage = p;
    this.loadBets();
  }

  // Its own ticket, like the report's: changing a filter fires a fresh request
  // without cancelling the one in flight, and a slow reply for the OLD filter
  // can land after a fast one for the new.
  private betsSeq = 0;

  loadBets() {
    const seq = ++this.betsSeq;
    this.betsLoading = true;
    this.betsError = "";

    const payload = this.basePayload();
    // A breakdown's controls mean nothing to a list of bets.
    delete payload.displayCount;
    payload.page = this.betsPage;
    payload.pageSize = this.betsPageSize;
    if (this.level === "rejectedbets") {
      payload.kind = "REJECTED";
    }

    this.appService
      .getMtsBets(payload)
      .pipe(timeout(REQUEST_TIMEOUT_MS))
      .subscribe(
        (res) => {
          if (seq !== this.betsSeq) {
            return;
          }
          this.betsLoading = false;
          if (res && res.status === "RS_OK") {
            this.bets = res.rows || [];
            this.betsTotal = res.total || 0;
            this.betsCapped = !!res.capped;
          } else {
            this.bets = [];
            this.betsTotal = 0;
            this.betsError = (res && res.errorDescription) || "Could not load the bets.";
          }
        },
        () => {
          if (seq !== this.betsSeq) {
            return;
          }
          this.betsLoading = false;
          this.bets = [];
          this.betsTotal = 0;
          this.betsError = "Could not load the bets.";
        }
      );
  }

  stamp(ms: number): string {
    return ms ? moment.utc(ms).format("DD MMM YYYY, HH:mm") + " UTC" : "-";
  }

  money(v: number): string {
    return (v || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  sign(v: number): string {
    return v > 0 ? "pos" : v < 0 ? "neg" : "";
  }

  countedClass(c: string): string {
    switch (c) {
      case "settled":
        return "is-settled";
      case "cashout":
        return "is-cashout";
      case "void":
        return "is-void";
      default:
        return "";
    }
  }

  private basePayload(): any {
    const p: any = {};
    // Only where it means something - see showRowCap. Sent on a level it does
    // not apply to, it would silently truncate a list that has no reason to be
    // cut and no control on screen to undo it with.
    if (this.showRowCap) {
      p.displayCount = this.displayCount;
      p.page = this.rowPage;
    }
    if (this.day > 0) {
      p.day = this.day;
    } else {
      p.startTime = Math.floor(new Date(this.selected.startDate).getTime() / 1000);
      p.endTime = Math.floor(new Date(this.selected.endDate).getTime() / 1000);
    }
    const ids = (list: any[], field: string) => (list || []).map((x) => x && x[field]).filter((x) => !!x);
    const ops = ids(this.selectedOperators, "operator_id");
    if (ops.length) {
      p.operatorIds = ops;
    }
    if (this.livePre) {
      p.livePre = this.livePre;
    }
    if (this.betKind) {
      p.betKind = this.betKind;
    }
    p.groupProvider = this.groupProvider;
    const sports = ids(this.selectedSports, "id");
    if (sports.length) {
      p.sportIds = sports;
    }
    // Only sent while the level above allows it - see the cascade. A stale
    // competition under a different sport would filter the report to nothing.
    if (!this.competitionDisabled) {
      const comps = ids(this.selectedCompetitions, "id");
      if (comps.length) {
        p.competitionIds = comps;
      }
    }
    if (!this.eventDisabled) {
      const evs = ids(this.selectedEvents, "id");
      if (evs.length) {
        p.eventIds = evs;
      }
    }
    return p;
  }

  // THE FILTERS DO NOT FETCH. Changing one marks the report stale and waits,
  // as the reference console does - a report over seven days of a busy book is
  // seconds of work, and firing one on every click of a multi-select spends
  // that over and over on filters nobody has finished choosing.
  dirty = false;

  markDirty() {
    this.dirty = true;
  }

  generate() {
    this.dirty = false;
    // A page number belongs to the report that was showing, not to the one
    // about to be asked for.
    this.rowPage = 1;
    this.betsPage = 1;
    this.load();
  }

  reset() {
    this.selectedOperators = [];
    this.selectedSports = [];
    this.selectedCompetitions = [];
    this.selectedEvents = [];
    this.competitionOptions = [];
    this.eventOptions = [];
    this.livePre = "";
    this.betKind = "";
    this.groupProvider = "SportsBook";
    this.requestedLevel = "sport";
    this.selected = { startDate: moment().startOf("day"), endDate: moment().endOf("day") };
    this.day = 0;
    this.load();
  }

  load() {
    // Stamped before anything is fetched, so the heading and the columns
    // describe the request in flight rather than the one that came back last.
    this.shownLevel = this.level;
    this.shownBetKind = this.betKind;

    // The ticket level is a DIFFERENT endpoint, not a groupBy. Asking the
    // aggregate for "bets" would be an unknown level and an error.
    if (this.isListLevel(this.level)) {
      this.rows = [];
      this.totals = null;
      this.totalRows = 0;
      this.loading = false;
      this.loadBets();
      return;
    }
    this.loading = true;
    this.errorMessage = "";
    const payload = this.basePayload();
    payload.groupBy = this.level;
    const seq = ++this.reportSeq;

    this.appService
      .getMtsReportingSummary(payload)
      .pipe(timeout(REQUEST_TIMEOUT_MS))
      .subscribe(
        (res) => {
          // Superseded: a newer report is already on its way, and this one
          // describes filters that are no longer on screen. It must not touch
          // the rows OR the loading flag - clearing that would take the
          // spinner off a report still in flight.
          if (seq !== this.reportSeq) {
            return;
          }
          this.loading = false;
          if (res && res.status === "RS_OK") {
            this.rows = this.mergeByName(res.rows || []);
            this.totals = res.totals || null;
            // The Singles band, as its own request. Fetched rather than
            // subtracted: the server's singles figures are what that column
            // must show, and deriving them here by taking multiples off the
            // total would put the counting rules in a second place.
            this.loadSinglesBand(payload, seq);
            // Absent unless the API cut the list, so it has to be cleared
            // rather than left over from the previous report.
            this.totalRows = res.totalRows || 0;
            // CLAMPED to what came back, because a filter can shrink the
            // report under a page number the reader is already on. The API
            // answers with its last page rather than an empty one; without
            // this the pager would go on claiming "page 7 of 3" beneath it.
            if (this.rowPage > this.rowPages) {
              this.rowPage = this.rowPages;
            }
            // The report at a level IS the option list for that level, so the
            // separate call for it is skipped. On a cold load that halves the
            // requests, and each one costs a Mongo aggregation plus a read of
            // every unflushed drawer.
            //
            // Only when nothing is selected at that level: a filtered report
            // lists what was asked for, not what there is to ask for, and
            // taking the options from it would leave the dropdown unable to
            // offer anything else.
            this.adoptRowsAsOptions();
            return;
          }
          // An error must EMPTY the table, never leave the previous filter's
          // numbers on screen under the new filter's heading.
          this.rows = [];
          this.totalRows = 0;
          this.totals = null;
          this.errorMessage = (res && res.errorDescription) || "The report could not be loaded.";
        },
        () => {
          if (seq !== this.reportSeq) {
            return;
          }
          this.loading = false;
          this.rows = [];
          this.totalRows = 0;
          this.totals = null;
          this.errorMessage = "The report could not be loaded.";
        }
      );
  }

  // The report just loaded is a breakdown at this.level, which is exactly what
  // the dropdown for that level needs - so it is used instead of asking again.
  private adoptRowsAsOptions() {
    // It fills a dropdown, so it counts as a load of that dropdown: without
    // claiming the sequence, an option request already in flight would land
    // afterwards and replace these rows with its own - or, worse, this would
    // overwrite a list the user has just caused to be fetched.
    const opts = this.rows
      .filter((r: any) => !!r.key)
      .map((r: any) => ({ id: r.key, name: r.label || r.key }));
    // NOT the sport list. That one comes from the catalog, deliberately - it
    // offers every sport in the book rather than only the ones with bets in
    // the range - and filling it from a breakdown here raced the catalog call
    // on every cold load, so which list you got depended on which response
    // was quicker.
    if (this.level === "competition" && !this.selectedCompetitions.length) {
      this.optionSeq.competition++;
      this.competitionOptions = opts;
    } else if (this.level === "event" && !this.selectedEvents.length) {
      this.optionSeq.event++;
      this.eventOptions = opts;
    }
  }

  // The option lists are breakdowns at that level over the CURRENT range and
  // filters, so they offer what has data rather than what exists. A failure
  // leaves the list empty, which filters nothing - never an error over a
  // report that loaded fine.
  private loadOptions(level: LevelName, assign: (opts: any[]) => void) {
    const payload = this.basePayload();
    payload.groupBy = level;
    // The list is of what EXISTS at that level, so it must not be narrowed by
    // a selection at that level OR AT ANY LEVEL BELOW IT.
    //
    // Stripping only its own ids left the children in place, and a child is a
    // far tighter filter than the parent: asking for the sport list while a
    // competition and an event were still selected returned the ONE sport
    // those belong to, so the dropdown collapsed to a single entry and no
    // other sport could be picked again without a Reset.
    const below: Record<LevelName, string[]> = {
      sport: ["sportIds", "competitionIds", "eventIds"],
      competition: ["competitionIds", "eventIds"],
      event: ["eventIds"],
      // Markets are the last breakdown, not a filter - there is no market
      // dropdown to populate - so nothing calls this with it. Present because
      // the map must be total.
      market: [],
      // Live/pre-match is not in the hierarchy and has no option list to
      // load - its filter is a fixed three-way choice, not a lookup - so
      // nothing calls this with it. Present because the map must be total:
      // leaving it out makes a level silently strip no filters at all if one
      // ever does.
      livepre: [],
      bets: [],
      rejectedbets: [],
    };
    for (const key of below[level]) {
      delete payload[key];
    }
    const seq = ++this.optionSeq[level];
    this.appService
      .getMtsReportingSummary(payload)
      .pipe(timeout(REQUEST_TIMEOUT_MS))
      .subscribe(
        (res) => {
          if (seq !== this.optionSeq[level]) {
            return;
          }
          if (res && res.status === "RS_OK") {
            assign(
              (res.rows || [])
                .filter((r: any) => !!r.key)
                .map((r: any) => ({ id: r.key, name: r.label || r.key }))
            );
            return;
          }
          // Empty, never stale. Leaving the previous filter's options in place
          // offers values that do not belong to the report on screen, and
          // picking one returns nothing for no visible reason.
          assign([]);
        },
        () => {
          if (seq !== this.optionSeq[level]) {
            return;
          }
          assign([]);
        }
      );
  }

  // From the CATALOG, not from a breakdown of the report.
  //
  // Deriving it from a groupBy=sport call offers exactly the sports with bets
  // in the range, but costs a full aggregation over that range every time a
  // filter changes - the most expensive query this path has. The catalog is a
  // few dozen rows behind a cache and does not move when the range does, so it
  // is fetched once per provider group. Picking a sport with no bets returns an
  // empty report, which is a correct answer.
  loadSportOptions() {
    const seq = ++this.optionSeq.sport;
    this.appService
      .getMtsSports({ groupProvider: this.groupProvider })
      .pipe(timeout(REQUEST_TIMEOUT_MS))
      .subscribe(
        (res: any) => {
          if (seq !== this.optionSeq.sport) {
            return;
          }
          const o =
            res && res.status === "RS_OK" && Array.isArray(res.sports) ? res.sports : [];
          this.sportOptions = o;
          // Replace a selection seeded from the URL with the resolved name, so
          // the chip reads "Cricket" rather than "sr:sport:21".
          this.selectedSports = this.selectedSports.map(
            (s) => o.find((x: any) => x.id === s.id) || s
          );
        },
        () => {
          if (seq !== this.optionSeq.sport) {
            return;
          }
          this.sportOptions = [];
        }
      );
  }

  loadCompetitionOptions() {
    this.loadOptions("competition", (o) => (this.competitionOptions = o));
  }

  loadEventOptions() {
    this.loadOptions("event", (o) => (this.eventOptions = o));
  }

  loadOperators() {
    const req = this.appService.getOperatorId();
    if (!req) {
      return;
    }
    req.pipe(timeout(REQUEST_TIMEOUT_MS)).subscribe(
      (res: any) => {
        if (!res || res.status !== "RS_OK" || !Array.isArray(res.operators)) {
          return;
        }
        // "all" is a real row in this deployment but it is not an operator any
        // bet is booked under, so selecting it filters to {op: "all"} and
        // reports an empty book - which reads as a quiet day rather than as a
        // bad filter. The summary page already drops it; this one did not.
        this.operatorData = res.operators
          .filter((o: any) => o && o.operator_id && o.operator_id !== "all")
          .sort((a: any, b: any) => a.operator_id.localeCompare(b.operator_id));
      },
      () => {
        // Leave the list empty; the report itself is unaffected.
      }
    );
  }

  onOperatorChange() {
    // The competition and event lists are breakdowns over the current
    // filters, so they move with the operator. The SPORT list does not: it is
    // the catalog for the chosen book, and /mts/sports takes the group
    // provider and nothing else - so re-fetching it here returned a
    // byte-identical list on every operator change.
    if (!this.competitionDisabled) {
      this.loadCompetitionOptions();
    }
    if (!this.eventDisabled) {
      this.loadEventOptions();
    }
  }

  // ---- rendering ----

  // A band cell can have no row behind it - a sport bet on only in combos has
  // no Singles figures, and the Singles totals are null until the second
  // request lands. The template guards the VALUE with *ngIf, but [ngClass] is
  // on the <td>, which is always created, so signClass() reaches here with
  // null on the first change-detection pass of every load.
  value(row: Row | null, col: Column): number {
    if (!row) {
      return 0;
    }
    const v = row[col.key];
    return typeof v === "number" ? v : 0;
  }

  format(row: Row, col: Column): string {
    const v = this.value(row, col);
    if (col.kind === "int") {
      return v.toLocaleString("en-IN");
    }
    if (col.kind === "pct") {
      return v.toFixed(2) + "%";
    }
    return v.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  signClass(row: Row | null, col: Column): string {
    if (!col.signed) {
      return "";
    }
    const v = this.value(row, col);
    return v > 0 ? "pos" : v < 0 ? "neg" : "";
  }

  back() {
    this.router.navigate(["/mts/reporting-summary"]);
  }

  downloadCsv() {
    // The bet list is a different table, so it is a different export. Built
    // from the SAME columns the page draws, so a download matches what was on
    // screen rather than being a second definition of the report.
    if (this.isBetList) {
      const esc = (v: any) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
      const tail = this.isRejectedList ? ["Reason", "Code"] : ["Status", "P&L"];
      const rows = [
        ["Date / Time", "Sport", "Event", "Market", "Selection", "Type",
          "Live/Pre", "Odds", "Stake", ...tail].map(esc).join(","),
      ];
      for (const b of this.bets) {
        const end = this.isRejectedList
          ? [b.reason || "Unknown", b.reasonCode || ""]
          : // Blank, not zero: an unsettled bet has no realised P&L, and a 0
            // in a spreadsheet column would be summed as though it had.
            [b.status, b.counted ? this.money(b.pnl) : ""];
        rows.push(
          [this.stamp(b.placedAt), b.sport, b.event, b.market, b.selection,
            b.betType, b.livePre, b.odds ? b.odds.toFixed(2) : "",
            this.money(b.stake), ...end].map(esc).join(",")
        );
      }
      const name = this.isRejectedList ? "mts-rejected-bets" : "mts-bets";
      this.saveCsv(rows, `${name}-${moment(this.selected.startDate).format("YYYYMMDD")}`);
      return;
    }

    // EVERY band, not just the first. With Multiples & Singles beside Singles
    // on screen, exporting only the combined half produced a file that looked
    // complete and was half the report - and no column said which half. Each
    // band's columns carry its name, so the two cannot be confused.
    const cols = this.visibleColumns;
    const bands = this.bands;
    const head = [this.levelHeading];
    for (const band of bands) {
      for (const c of cols) {
        head.push(bands.length > 1 ? `${band.title} - ${c.label}` : c.label);
      }
    }
    const lines = [head.map((h) => `"${h}"`).join(",")];
    for (const r of this.rows) {
      const line = [`"${r.label}"`];
      for (const band of bands) {
        const cellRow = this.cellRow(r, band);
        // Empty, not zero: a sport bet on only in combos has no singles
        // figures, and a 0 in a spreadsheet column would be summed.
        for (const c of cols) {
          line.push(cellRow ? String(this.value(cellRow, c)) : "");
        }
      }
      lines.push(line.join(","));
    }
    if (this.totals) {
      const line = ["Total"];
      for (const band of bands) {
        const totalsRow = this.bandTotals(band);
        for (const c of cols) {
          line.push(totalsRow ? String(this.value(totalsRow, c)) : "");
        }
      }
      lines.push(line.join(","));
    }
    this.saveCsv(
      lines,
      `mts-${this.level}-${moment(this.selected.startDate).format(
        "YYYYMMDD"
      )}-${moment(this.selected.endDate).format("YYYYMMDD")}`
    );
  }

  private saveCsv(lines: string[], name: string) {
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${name}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }
}
